/**
 * The wallet top-up, one step at a time (Warren, 2026-10-25: "There should
 * be a confirmation before topping up, now you hit an option and it bills
 * your card.").
 *
 * Before: each preset chip (+$10, +$25, ...) called POST /wallet/topup
 * itself, an off-session charge on the saved card, on the first tap.
 *
 * Now nothing charges until the driver confirms:
 *
 *   edit      a chip or the amount field only sets the amount
 *   confirm   "Top up" opens the confirmation (amount and currency, the
 *             card, the wallet that receives it), when the amount passes
 *             the existing rules (minimum top-up, a card on file)
 *   charging  "Confirm" sends the one top-up; a second tap while it runs
 *             sends nothing
 *
 * "Cancel" closes the confirmation and sends nothing. The reducer and the
 * actions are pure (no React, no DOM), so node tests drive the exact
 * handlers WalletPage wires to the chips, the button and the dialog.
 */
import type { TopupResult } from '@/lib/api';

/** The preset chips, in cents of the top-up's currency. */
export const QUICK_AMOUNTS = [1000, 2500, 5000, 10000];

export type TopupPhase = 'edit' | 'confirm' | 'charging';

export interface TopupFlowState {
  amountCents: number | '';
  phase: TopupPhase;
}

export type TopupFlowAction =
  | { type: 'amount'; cents: number | '' }
  | { type: 'review' }
  | { type: 'cancel' }
  | { type: 'charging' }
  | { type: 'settled'; clearAmount: boolean };

export function topupFlowReducer(state: TopupFlowState, action: TopupFlowAction): TopupFlowState {
  switch (action.type) {
    // The amount changes only while editing: never under an open dialog.
    case 'amount': return state.phase === 'edit' ? { ...state, amountCents: action.cents } : state;
    case 'review': return state.phase === 'edit' ? { ...state, phase: 'confirm' } : state;
    case 'cancel': return state.phase === 'confirm' ? { ...state, phase: 'edit' } : state;
    case 'charging': return state.phase === 'confirm' ? { ...state, phase: 'charging' } : state;
    case 'settled': return { amountCents: action.clearAmount ? '' : state.amountCents, phase: 'edit' };
    default: return state;
  }
}

/** The rules a top-up must pass before the confirmation opens (unchanged:
 *  the operator's minimum top-up and a saved card). */
export interface TopupRules {
  minTopupCents: number;
  hasPaymentMethod: boolean;
}

export function topupAmountValid(amount: number | '', rules: TopupRules): amount is number {
  return typeof amount === 'number'
    && Number.isSafeInteger(amount)
    && amount > 0
    && amount >= rules.minTopupCents
    && rules.hasPaymentMethod;
}

/** Where the top-up goes and which card pays it, read when Confirm is tapped. */
export interface TopupOrder {
  /** Sent only when the driver has more than one currency in play, as before. */
  currency?: string;
  /** The card the confirmation names, when known: the card shown is the card charged. */
  paymentMethodId?: string;
}

export interface TopupActionDeps {
  getState: () => TopupFlowState;
  dispatch: (action: TopupFlowAction) => void;
  rules: () => TopupRules;
  order: () => TopupOrder;
  /** POST /wallet/topup (topupWallet). */
  charge: (amountCents: number, paymentMethodId?: string, currency?: string) => Promise<TopupResult>;
  onSuccess: (result: TopupResult) => void;
  onError: (err: unknown) => void;
  /** Clears the last result and error when the driver starts a new top-up. */
  onReset?: () => void;
}

export interface TopupActions {
  /** A preset chip: fills the amount. Never charges. */
  pick: (cents: number) => void;
  /** The amount field. Never charges. */
  type: (cents: number | '') => void;
  /** "Top up": opens the confirmation when the amount passes the rules. Never charges. */
  review: () => boolean;
  /** "Cancel" in the confirmation. Never charges. */
  cancel: () => void;
  /** "Confirm": the one call that charges, at most once at a time. */
  confirm: () => Promise<void>;
}

export function createTopupActions(deps: TopupActionDeps): TopupActions {
  let inFlight = false;
  return {
    pick(cents) {
      if (deps.getState().phase !== 'edit') return;
      deps.onReset?.();
      deps.dispatch({ type: 'amount', cents });
    },
    type(cents) {
      if (deps.getState().phase !== 'edit') return;
      deps.dispatch({ type: 'amount', cents });
    },
    review() {
      const state = deps.getState();
      if (state.phase !== 'edit' || !topupAmountValid(state.amountCents, deps.rules())) return false;
      deps.onReset?.();
      deps.dispatch({ type: 'review' });
      return true;
    },
    cancel() {
      if (inFlight) return;
      deps.dispatch({ type: 'cancel' });
    },
    async confirm() {
      const state = deps.getState();
      if (inFlight || state.phase !== 'confirm' || !topupAmountValid(state.amountCents, deps.rules())) return;
      inFlight = true;
      deps.dispatch({ type: 'charging' });
      const { currency, paymentMethodId } = deps.order();
      try {
        const result = await deps.charge(state.amountCents, paymentMethodId, currency);
        deps.onSuccess(result);
        deps.dispatch({ type: 'settled', clearAmount: true });
      } catch (err) {
        deps.onError(err);
        deps.dispatch({ type: 'settled', clearAmount: false });
      } finally {
        inFlight = false;
      }
    },
  };
}
